import {
  getAuth,
  signInWithCustomToken,
  signOut as firebaseSignOut,
} from "firebase/auth";
import { DevAuthSyncService } from "./devAuthSync.service";

export interface SessionAuthResult {
  success: boolean;
  error?: string;
  customToken?: string;
  uid?: string;
}

/**
 * OPTIMIZED Session-based authentication service for cross-subdomain auth
 * Minimizes Firebase function calls and focuses on user experience
 *
 * SECURITY NOTE: Development mode bypasses session auth to avoid CORS issues
 * but maintains full Firebase Auth security. Production always uses full session auth.
 */
export class OptimizedSessionAuthService {
  // SECURITY: Strict development detection - must be localhost AND development build
  private static readonly IS_DEVELOPMENT =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1" ||
      window.location.hostname.includes("localhost")) &&
    // CRITICAL: Only bypass in actual development builds, never in production
    (typeof process === "undefined" ||
      process.env.NODE_ENV === "development") &&
    // EXTRA SAFETY: Ensure we're not on any zurilink.com domain
    !window.location.hostname.includes("zurilink.com");
  private static readonly CSRF_COOKIE_NAME = "csst";
  private static sessionCreated = false;
  private static lastSessionCheck = 0;
  private static readonly SESSION_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes
  private static sessionCreationInProgress = false;
  private static lastSessionCreation = 0;
  private static readonly SESSION_CREATION_THROTTLE = 30 * 1000; // 30 seconds
  private static logoutInProgress = false;

  // Session establishment grace period to prevent race conditions

  /**
   * Enhanced security alert system with comprehensive context
   */
  private static async sendSecurityAlert(
    alertType: string,
    details: any,
    severity: "LOW" | "MEDIUM" | "HIGH" | "CRITICAL" = "HIGH"
  ): Promise<void> {
    try {
      // Get current user context if available
      const auth = getAuth();
      const currentUser = auth.currentUser;

      // Gather comprehensive security context
      const securityContext = {
        // Alert details
        alertType,
        severity,
        timestamp: new Date().toISOString(),
        source: "OptimizedSessionAuthService",

        // User context
        userId: currentUser?.uid || "anonymous",
        userEmail: currentUser?.email || "unknown",
        userVerified: currentUser?.emailVerified || false,

        // Session context
        domain: window.location.hostname,
        url: window.location.href,
        referrer: document.referrer || "direct",
        userAgent: navigator.userAgent,

        // Browser context
        language: navigator.language,
        cookiesEnabled: navigator.cookieEnabled,
        onlineStatus: navigator.onLine,

        // Custom details
        ...details,

        // Security metadata
        sessionId: this.generateSessionId(),
        clientFingerprint: this.generateClientFingerprint(),
      };

      // Send to Firebase Function for staff notification
      await fetch("/api/securityAlert", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(securityContext),
      });
    } catch (error) {
      // Fallback: Store in localStorage for staff to check
      const alerts = JSON.parse(
        localStorage.getItem("security_alerts") || "[]"
      );
      alerts.push({
        alertType,
        details,
        timestamp: new Date().toISOString(),
        severity,
        userId: getAuth()?.currentUser?.uid || "anonymous",
        domain: window.location.hostname,
      });
      localStorage.setItem(
        "security_alerts",
        JSON.stringify(alerts.slice(-50))
      ); // Keep last 50
    }
  }

  /**
   * Generate session ID for tracking
   */
  private static generateSessionId(): string {
    return `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Generate client fingerprint for tracking
   */
  private static generateClientFingerprint(): string {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    ctx?.fillText("fingerprint", 2, 2);
    const canvasFingerprint = canvas.toDataURL();

    const fingerprint = {
      screen: `${screen.width}x${screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      platform: navigator.platform,
      canvas: canvasFingerprint.slice(-50), // Last 50 chars
    };

    return btoa(JSON.stringify(fingerprint)).slice(0, 32);
  }

  /**
   * Get the CSRF token from cookies
   */
  private static getCsrfToken(): string | null {
    const cookies = document.cookie.split(";");
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split("=");
      if (name === this.CSRF_COOKIE_NAME) {
        return decodeURIComponent(value);
      }
    }
    return null;
  }

  /**
   * Get the base URL for session auth functions
   */
  private static getSessionAuthBaseUrl(): string {
    const hostname = window.location.hostname;
    const isLocalhost = hostname.includes("localhost");
    const isCustomDomain =
      hostname === "zurilink.com" || hostname.endsWith(".zurilink.com");

    if (isLocalhost) {
      // Development: Use direct Cloud Function URLs since /api routes don't exist in dev
      return "https://us-central1-zurilink-28b09.cloudfunctions.net";
    } else if (isCustomDomain) {
      // Production custom domain: Use /api routes
      return "/api";
    } else {
      // Firebase hosting domains: Use /api routes
      return "/api";
    }
  }

  /**
   * OPTIMIZED: Create a session after successful Firebase Auth sign-in
   * Only called ONCE per sign-in, not on every auth state change
   */
  static async createSession(): Promise<SessionAuthResult> {
    // CRITICAL: Don't create session if logout is in progress
    if (this.logoutInProgress) {
      return { success: false, error: "Logout in progress" };
    }

    // Skip session creation in development to avoid CORS issues
    if (this.IS_DEVELOPMENT) {
      // SECURITY: Double-check we're actually in development
      if (window.location.hostname.includes("zurilink.com")) {
        // Send security alert to staff
        this.sendSecurityAlert("development_bypass_attempt", {
          domain: window.location.hostname,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
          method: "createSession",
        });

        // Fall through to production session auth
      } else {
        // Verify Firebase Auth is working
        const auth = getAuth();
        if (!auth.currentUser) {
          return {
            success: false,
            error: "No authenticated user in development",
          };
        }

        // In development mode, skip session creation entirely
        this.sessionCreated = true;
        return { success: true, error: "Development mode - no session needed" };
      }
    }

    // Use session-based auth for production only
    const isLocalhost = window.location.hostname.includes("localhost");
    if (isLocalhost) {
      // In development, also store auth state for debugging but still create session
      await DevAuthSyncService.storeAuthState();
    }

    // AGGRESSIVE THROTTLING: Prevent rapid session creation calls
    const now = Date.now();
    if (this.sessionCreationInProgress) {
      return { success: true };
    }

    if (now - this.lastSessionCreation < this.SESSION_CREATION_THROTTLE) {
      return { success: true };
    }

    // Avoid duplicate session creation (more aggressive checking)
    if (this.sessionCreated) {
      return { success: true };
    }

    // Also check if we already have session cookies before making a new request
    const existingCsrfToken = this.getCsrfToken();
    if (existingCsrfToken) {
      this.sessionCreated = true;
      return { success: true };
    }

    try {
      // Mark session creation as in progress
      this.sessionCreationInProgress = true;
      this.lastSessionCreation = now;

      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        this.sessionCreationInProgress = false;
        return {
          success: false,
          error: "User not authenticated",
        };
      }

      // Get fresh ID token
      const idToken = await user.getIdToken();

      const baseUrl = this.getSessionAuthBaseUrl();
      const response = await fetch(`${baseUrl}/usersSignin`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Important for cookies
        body: JSON.stringify({ idToken }),
      });

      if (!response.ok) {
        let errorMessage = "Failed to create session";
        try {
          const error = await response.json();
          errorMessage = error.error || errorMessage;
        } catch (parseError) {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        return {
          success: false,
          error: errorMessage,
        };
      }

      const result = await response.json();
      this.sessionCreated = true;
      this.sessionCreationInProgress = false;

      return {
        success: true,
        uid: result.uid,
      };
    } catch (error: any) {
      this.sessionCreationInProgress = false; // Reset on error
      return {
        success: false,
        error: error.message || "Failed to create session",
      };
    }
  }

  /**
   * OPTIMIZED: Check authentication status and sign in with session if available
   * Only called when needed, with throttling to prevent excessive calls
   */
  static async checkAuthStatus(): Promise<SessionAuthResult> {
    try {
      // Don't check session if logout is in progress
      if (this.logoutInProgress) {
        return { success: false, error: "Logout in progress" };
      }

      // Skip session check in development to avoid CORS issues
      if (this.IS_DEVELOPMENT) {
        // SECURITY: Double-check we're actually in development
        if (window.location.hostname.includes("zurilink.com")) {
          // Send security alert to staff
          this.sendSecurityAlert("development_bypass_attempt", {
            domain: window.location.hostname,
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
            method: "checkAuthStatus",
          });

          // Fall through to production session auth
        } else {
          const auth = getAuth();
          if (auth.currentUser) {
            return { success: true, uid: auth.currentUser.uid };
          } else {
            return {
              success: false,
              error: "No authenticated user in development",
            };
          }
        }
      }

      const auth = getAuth();

      // If already signed in with Firebase Auth, no need to check session
      if (auth.currentUser) {
        return {
          success: true,
          uid: auth.currentUser.uid,
        };
      }

      // Use session-based auth for all environments (localhost and production)

      // Throttle session checks to prevent excessive function calls
      const now = Date.now();
      if (now - this.lastSessionCheck < this.SESSION_CHECK_INTERVAL) {
        return {
          success: false,
          error: "Session check throttled",
        };
      }
      this.lastSessionCheck = now;

      // Get CSRF token from cookies
      const csrfToken = this.getCsrfToken();
      if (!csrfToken) {
        return {
          success: false,
          error: "No session found",
        };
      }

      const baseUrl = this.getSessionAuthBaseUrl();
      const response = await fetch(`${baseUrl}/usersCheckAuthStatus`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${csrfToken}`,
        },
        credentials: "include", // Important for cookies
      });

      if (!response.ok) {
        let errorMessage = "No valid session";
        try {
          const error = await response.json();
          errorMessage = error.error || errorMessage;
        } catch (parseError) {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        return {
          success: false,
          error: errorMessage,
        };
      }

      const result = await response.json();

      if (result.customToken) {
        // Sign in with the custom token
        await signInWithCustomToken(auth, result.customToken);

        return {
          success: true,
          customToken: result.customToken,
          uid: result.uid,
        };
      }

      return {
        success: false,
        error: "No custom token received",
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || "Failed to check auth status",
      };
    }
  }

  /**
   * OPTIMIZED: Sign out and clear session cookies
   * Called once when user explicitly signs out
   */
  static async signOut(): Promise<SessionAuthResult> {
    try {
      // Set logout flag to prevent immediate re-login
      this.logoutInProgress = true;

      // Sign out from Firebase Auth first
      const auth = getAuth();
      await firebaseSignOut(auth);

      // Skip server-side logout in development to avoid CORS issues
      if (this.IS_DEVELOPMENT) {
        // Reset local state
        this.sessionCreated = false;
        this.lastSessionCheck = 0;
        this.sessionCreationInProgress = false;
        this.lastSessionCreation = 0;

        // Clear logout flag after delay
        setTimeout(() => {
          this.logoutInProgress = false;
        }, 2000);

        return { success: true };
      }

      // Clear session cookies (production only)
      const baseUrl = this.getSessionAuthBaseUrl();
      await fetch(`${baseUrl}/usersSignout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Important for cookies
      });

      // Reset session state
      this.sessionCreated = false;
      this.lastSessionCheck = 0;
      this.sessionCreationInProgress = false;
      this.lastSessionCreation = 0;

      // Clear development auth state if in localhost
      const isLocalhost = window.location.hostname.includes("localhost");
      if (isLocalhost) {
        DevAuthSyncService.clearAuthState();
      }

      // Clear logout flag after a delay to prevent race conditions
      setTimeout(() => {
        this.logoutInProgress = false;
      }, 2000); // 2 second delay

      return {
        success: true,
      };
    } catch (error: any) {
      // Clear logout flag even on error
      setTimeout(() => {
        this.logoutInProgress = false;
      }, 2000);
      return {
        success: false,
        error: error.message || "Failed to sign out",
      };
    }
  }

  /**
   * Force an immediate session check, bypassing cache
   * Useful when returning from cross-domain transfers
   */
  static async forceSessionCheck(): Promise<SessionAuthResult> {
    // Don't check session if logout is in progress
    if (this.logoutInProgress) {
      return { success: false, error: "Logout in progress" };
    }

    // Skip session check in development to avoid CORS issues
    if (this.IS_DEVELOPMENT) {
      // SECURITY: Double-check we're actually in development
      if (window.location.hostname.includes("zurilink.com")) {
        // Send security alert to staff
        this.sendSecurityAlert("development_bypass_attempt", {
          domain: window.location.hostname,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
          method: "forceSessionCheck",
        });

        // Fall through to production logic
      } else {
        return this.checkAuthStatus(); // Will return Firebase Auth status
      }
    }

    // Force a fresh session check for production only
    this.lastSessionCheck = 0; // Reset cache
    this.sessionCreated = false; // Reset session created flag
    return this.checkAuthStatus();
  }

  /**
   * OPTIMIZED: Monitor session state for cross-domain sign-out detection
   * Checks both CSRF cookie and active session validation
   */
  static monitorSessionState(onSignOut: () => void): () => void {
    let lastCsrfToken = this.getCsrfToken();
    let sessionCheckCounter = 0;
    let isPaused = false;

    const doCheck = async () => {
      if (isPaused) return;

      const currentCsrfToken = this.getCsrfToken();

      if (lastCsrfToken && !currentCsrfToken) {
        const auth = getAuth();
        await firebaseSignOut(auth);
        this.sessionCreated = false;
        this.lastSessionCheck = 0;
        onSignOut();
        return;
      }

      // Every 20 checks (~5 minutes at 15s interval), actively verify session
      sessionCheckCounter++;
      if (sessionCheckCounter >= 20 && currentCsrfToken) {
        sessionCheckCounter = 0;

        try {
          const sessionResult = await this.checkAuthStatus();
          if (!sessionResult.success) {
            const auth = getAuth();
            await firebaseSignOut(auth);
            this.sessionCreated = false;
            this.lastSessionCheck = 0;
            onSignOut();
            return;
          }
        } catch (error) {
          // Don't sign out on network errors, only on explicit session invalidation
        }
      }

      lastCsrfToken = currentCsrfToken;
    };

    const checkInterval = setInterval(doCheck, 15000); // 15 seconds

    // Pause monitoring when tab is hidden to save Cloud Function invocations
    const handleVisibility = () => {
      isPaused = document.hidden;
      if (!document.hidden) {
        // Force an immediate check when tab becomes visible
        sessionCheckCounter = 19;
        doCheck();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      clearInterval(checkInterval);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }

  /**
   * OPTIMIZED: Reset session state (for testing/debugging)
   */
  static resetSessionState(): void {
    this.sessionCreated = false;
    this.lastSessionCheck = 0;
  }
}
